import React from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';

interface TrialCountdownWidgetProps {
    daysRemaining: number;
    status: 'active' | 'trialing' | 'expired';
}

/**
 * Premium Trial Countdown Widget
 * Follows Cherry Flow Design System: Glassmorphism, Micro-animations, Dynamic Urgency.
 */
const TrialCountdownWidget: React.FC<TrialCountdownWidgetProps> = ({ daysRemaining, status }) => {
    if (status === 'expired') return null;

    // Urgency Levels based on PRD
    const isCritical = daysRemaining <= 2;
    const isWarning = daysRemaining <= 4 && daysRemaining > 2;

    // Dynamic Styles
    const urgencyClasses = isCritical
        ? 'border-red-500/30 bg-red-500/10 text-red-400'
        : isWarning
            ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
            : 'border-purple-500/30 bg-purple-500/10 text-purple-400';

    const pulseClass = isCritical ? 'animate-pulse' : '';

    return (
        <div className={`trial-widget relative overflow-hidden rounded-xl border px-4 py-3 backdrop-blur-md transition-all duration-500 hover:shadow-lg ${urgencyClasses}`}>
            {/* Background Glow */}
            <div className={`absolute -right-4 -top-4 h-16 w-16 rounded-full blur-2xl opacity-20 ${isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-purple-500'}`} />

            <div className="relative flex items-center gap-3">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 border border-white/10 ${pulseClass}`}>
                    <ClockIcon className="h-5 w-5" />
                </div>

                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest font-bold opacity-70">
                        {status === 'trialing' ? 'Trial Period' : 'Subscription'}
                    </span>
                    <div className="flex items-baseline gap-1">
                        <span className="text-lg font-black leading-none">{daysRemaining}</span>
                        <span className="text-xs font-medium opacity-80">{daysRemaining === 1 ? 'Day' : 'Days'} Left</span>
                    </div>
                </div>
            </div>

            {/* Progress Bar (Visual) */}
            <div className="mt-3 h-1 w-full rounded-full bg-black/20 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-1000 ${isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-purple-500'}`}
                    style={{ width: `${(daysRemaining / 7) * 100}%` }}
                />
            </div>
        </div>
    );
};

export default TrialCountdownWidget;
